// src/pages/RunLogPage.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, CheckCircle, XCircle, ExternalLink, FileText } from 'lucide-react';
import { getRuns } from '../utils/api';

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

export default function RunLogPage() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['runs'],
    queryFn: () => getRuns(100),
    refetchInterval: 10000,
  });

  const runs = data?.runs || [];
  const stats = data?.stats;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Run Log</h1>
        <p className="page-subtitle">History of all pipeline executions and their outcomes.</p>
      </div>

      {stats && (
        <div className="stat-grid" style={{ marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-label">Total Runs</div>
            <div className="stat-value">{stats.totalRuns}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Success Rate</div>
            <div className="stat-value" style={{ color: 'var(--green)' }}>
              {stats.totalRuns > 0 ? Math.round((stats.succeeded / stats.totalRuns) * 100) : 0}%
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Tokens</div>
            <div className="stat-value">{stats.totalTokensUsed?.toLocaleString()}</div>
            <div className="stat-sub">≈ ${stats.estimatedCost}</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={14} />
            Execution History
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => refetch()}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {isLoading ? (
          <div className="empty-state"><span className="spinner" /></div>
        ) : runs.length === 0 ? (
          <div className="empty-state">No runs yet — trigger a pipeline run from the Dashboard.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>File</th>
                  <th>Report Type</th>
                  <th>Tokens</th>
                  <th>Emails</th>
                  <th>When</th>
                  <th>Drive</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(run => (
                  <tr key={run.id}>
                    <td>
                      {run.status === 'success'
                        ? <span className="badge badge-success"><CheckCircle size={11} /> success</span>
                        : run.status === 'error'
                        ? <span className="badge badge-error"><XCircle size={11} /> error</span>
                        : <span className="badge badge-pending">{run.status}</span>
                      }
                    </td>
                    <td>
                      <span className="truncate mono" style={{ fontSize: 12 }}>{run.filename || '—'}</span>
                      {run.error && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>{run.error}</div>}
                    </td>
                    <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{run.reportType?.replace(/-/g, ' ') || '—'}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{run.tokensUsed?.toLocaleString() || '—'}</td>
                    <td style={{ fontSize: 13 }}>{run.emailsSent ?? '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{timeAgo(run.timestamp)}</td>
                    <td>
                      {run.driveAnalysisLink
                        ? <a href={run.driveAnalysisLink} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                            <ExternalLink size={11} />
                          </a>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
